import React, { useState } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { motion } from "framer-motion";
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { ArrowLeft, Pencil, Zap, Users, Building2, TrendingUp, ArrowDownToLine, ArrowUpFromLine, CheckCircle2, XCircle, Clock, Activity, Search, ChevronLeft, ChevronRight, Percent, Receipt, Eye } from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";

const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  suspended: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
};
const COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

export default function AdminEngineDetail() {
  const urlParams = new URLSearchParams(window.location.search);
  const engineId = urlParams.get("id");
  
  const [selectedTx, setSelectedTx] = useState(null);
  const [txFilters, setTxFilters] = useState({ search: "", status: "all", type: "all", reseller: "all", merchant: "all" });
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // TODO: Implement custom API calls
  const { data: engine } = useQuery({
    queryKey: ["engine-detail", engineId],
    queryFn: async () => {
      // TODO: Implement Engine.filter API call
      return null;
    },
    enabled: !!engineId,
  });

  const { data: resellers = [] } = useQuery({ queryKey: ["resellers"], queryFn: async () => [] }); // TODO: Implement Reseller.list API call
  const { data: merchants = [] } = useQuery({ queryKey: ["merchants"], queryFn: async () => [] }); // TODO: Implement Merchant.list API call
  const { data: transactions = [], isLoading: txLoading } = useQuery({ queryKey: ["transactions"], queryFn: async () => [] }); // TODO: Implement Transaction.list API call
  const { data: providers = [] } = useQuery({ queryKey: ["providers"], queryFn: async () => [] }); // TODO: Implement Provider.list API call

  if (!engine) return <div className="flex items-center justify-center min-h-[60vh] text-slate-500">Loading...</div>;

  const status = statusConfig[engine.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  
  // Today's stats
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const todayTxs = transactions.filter(t => {
    const txDate = new Date(t.created_date);
    return txDate >= todayStart && txDate <= todayEnd;
  });
  const todayVolume = todayTxs.filter(t => t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  
  const totalVolume = transactions.filter(t => t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalDeposits = transactions.filter(t => t.type === "deposit" && t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalWithdrawals = transactions.filter(t => t.type === "withdrawal" && t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const successCount = transactions.filter(t => t.status === "success").length;
  const failCount = transactions.filter(t => t.status === "failed").length;
  const successRate = transactions.length > 0 ? ((successCount / transactions.length) * 100).toFixed(1) : 0;

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    const dayTxs = transactions.filter(t => new Date(t.created_date).toDateString() === date.toDateString() && t.status === "success");
    return { 
      date: format(date, "MMM d"), 
      deposits: dayTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
      withdrawals: dayTxs.filter(t => t.type === "withdrawal").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
    };
  });

  const providerData = (engine.allowed_providers || ["SCB", "KBANK", "BBL"]).map((code, i) => ({ name: code, value: Math.floor(Math.random() * 100) + 20 }));

  // Filter transactions
  const filteredTxs = transactions.filter(tx => {
    if (txFilters.search) {
      const search = txFilters.search.toLowerCase();
      if (!tx.transaction_id?.toLowerCase().includes(search) && !tx.merchant_ref?.toLowerCase().includes(search)) return false;
    }
    if (txFilters.status !== "all" && tx.status !== txFilters.status) return false;
    if (txFilters.type !== "all" && tx.type !== txFilters.type) return false;
    if (txFilters.merchant !== "all" && tx.merchant_id !== txFilters.merchant) return false;
    return true;
  });

  const paginatedTxs = filteredTxs.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredTxs.length / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link to={createPageUrl("AdminEngines")}><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-sky-100 flex items-center justify-center"><Zap className="h-8 w-8 text-sky-600" /></div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">{engine.name}</h1>
                <Badge className={`${status.color} gap-1 border-0`}><StatusIcon className="h-3 w-3" />{status.label}</Badge>
              </div>
              <p className="text-slate-500">{engine.code} • {engine.user_email}</p>
            </div>
          </div>
        </div>
        <Link to={createPageUrl("AdminEngineForm") + `?id=${engineId}`}><Button className="gap-2"><Pencil className="h-4 w-4" />Edit</Button></Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {[
          { title: "Today's Volume", value: `฿${todayVolume.toLocaleString()}`, icon: TrendingUp, color: "bg-blue-100 text-blue-600" },
          { title: "Total Volume", value: `฿${totalVolume.toLocaleString()}`, icon: Activity, color: "bg-emerald-100 text-emerald-600" },
          { title: "Success Rate", value: `${successRate}%`, icon: Percent, color: "bg-purple-100 text-purple-600" },
          { title: "Resellers", value: resellers.length, icon: Users, color: "bg-amber-100 text-amber-600" },
          { title: "Merchants", value: merchants.length, icon: Building2, color: "bg-sky-100 text-sky-600" },
          { title: "Transactions", value: transactions.length, icon: Receipt, color: "bg-pink-100 text-pink-600" },
        ].map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center`}><stat.icon className="h-5 w-5" /></div>
              <div><p className="text-xs text-slate-500">{stat.title}</p><p className="text-lg font-bold">{stat.value}</p></div>
            </div>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="analytics" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="analytics" className="gap-2"><Activity className="h-4 w-4" />Analytics</TabsTrigger>
          <TabsTrigger value="transactions" className="gap-2"><Receipt className="h-4 w-4" />All Transactions</TabsTrigger>
          <TabsTrigger value="resellers" className="gap-2"><Users className="h-4 w-4" />Resellers</TabsTrigger>
          <TabsTrigger value="merchants" className="gap-2"><Building2 className="h-4 w-4" />Merchants</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2">
              <Card><CardHeader><CardTitle>Volume (7 Days)</CardTitle></CardHeader><CardContent>
                <div className="h-64"><ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="depositGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                      <linearGradient id="withdrawGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="date" stroke="#94a3b8" fontSize={12} /><YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `${v}K`} />
                    <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "12px" }} />
                    <Area type="monotone" dataKey="deposits" name="Deposits" stroke="#3b82f6" strokeWidth={2} fill="url(#depositGrad)" />
                    <Area type="monotone" dataKey="withdrawals" name="Withdrawals" stroke="#10b981" strokeWidth={2} fill="url(#withdrawGrad)" />
                  </AreaChart>
                </ResponsiveContainer></div>
              </CardContent></Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card><CardHeader><CardTitle>Provider Distribution</CardTitle></CardHeader><CardContent>
                <div className="h-64"><ResponsiveContainer width="100%" height="100%">
                  <PieChart><Pie data={providerData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                    {providerData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie><Tooltip /></PieChart>
                </ResponsiveContainer></div>
                <div className="flex flex-wrap gap-2 mt-4 justify-center">{providerData.map((e, i) => <div key={e.name} className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="text-sm">{e.name}</span></div>)}</div>
              </CardContent></Card>
            </motion.div>
            {/* Recent Transactions */}
            <div className="lg:col-span-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Recent 20 Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <AdminTransactionTable 
                    transactions={transactions.slice(0, 20)} 
                    isLoading={txLoading} 
                    onView={setSelectedTx} 
                    compact 
                    merchants={merchants}
                    resellers={resellers}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* All Transactions Tab */}
        <TabsContent value="transactions">
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-4 border border-slate-100">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search by ID or reference..." value={txFilters.search} onChange={(e) => { setTxFilters({ ...txFilters, search: e.target.value }); setPage(0); }} className="pl-10" />
              </div>
              <Select value={txFilters.reseller} onValueChange={(v) => { setTxFilters({ ...txFilters, reseller: v }); setPage(0); }}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Resellers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resellers</SelectItem>
                  {resellers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={txFilters.merchant} onValueChange={(v) => { setTxFilters({ ...txFilters, merchant: v }); setPage(0); }}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Merchants" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Merchants</SelectItem>
                  {merchants.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={txFilters.type} onValueChange={(v) => { setTxFilters({ ...txFilters, type: v }); setPage(0); }}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="deposit">Deposits</SelectItem>
                  <SelectItem value="withdrawal">Withdrawals</SelectItem>
                </SelectContent>
              </Select>
              <Select value={txFilters.status} onValueChange={(v) => { setTxFilters({ ...txFilters, status: v }); setPage(0); }}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <AdminTransactionTable 
              transactions={paginatedTxs} 
              isLoading={txLoading} 
              onView={setSelectedTx}
              merchants={merchants}
              resellers={resellers}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, filteredTxs.length)} of {filteredTxs.length}</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm text-slate-600">Page {page + 1} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="resellers">
          <Card><CardHeader><CardTitle>Resellers ({resellers.length})</CardTitle></CardHeader><CardContent>
            <Table><TableHeader><TableRow><TableHead>Reseller</TableHead><TableHead>Code</TableHead><TableHead>Status</TableHead><TableHead>Merchants</TableHead><TableHead>Created</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{resellers.map(r => <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50"><TableCell><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center"><Users className="h-4 w-4 text-purple-600" /></div><div><p className="font-medium">{r.name}</p><p className="text-sm text-slate-500">{r.user_email}</p></div></div></TableCell><TableCell><Badge variant="outline">{r.code}</Badge></TableCell><TableCell><Badge className={statusConfig[r.status]?.color || "bg-slate-100"}>{r.status}</Badge></TableCell><TableCell>{r.max_merchants || 10}</TableCell><TableCell className="text-slate-500">{r.created_date ? format(new Date(r.created_date), "MMM d, yyyy") : "N/A"}</TableCell><TableCell><Link to={createPageUrl("AdminResellerDetail") + `?id=${r.id}`}><Button variant="ghost" size="icon"><Eye className="h-4 w-4" /></Button></Link></TableCell></TableRow>)}</TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="merchants">
          <Card><CardHeader><CardTitle>Merchants ({merchants.length})</CardTitle></CardHeader><CardContent>
            <Table><TableHeader><TableRow><TableHead>Merchant</TableHead><TableHead>Code</TableHead><TableHead>Status</TableHead><TableHead>Fee</TableHead><TableHead>Created</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{merchants.map(m => <TableRow key={m.id} className="cursor-pointer hover:bg-slate-50"><TableCell><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><Building2 className="h-4 w-4 text-blue-600" /></div><div><p className="font-medium">{m.name}</p><p className="text-sm text-slate-500">{m.contact_email}</p></div></div></TableCell><TableCell><Badge variant="outline">{m.code}</Badge></TableCell><TableCell><Badge className={statusConfig[m.status]?.color || "bg-slate-100"}>{m.status}</Badge></TableCell><TableCell>{m.fee_value}{m.fee_type === "percentage" ? "%" : " THB"}</TableCell><TableCell className="text-slate-500">{m.created_date ? format(new Date(m.created_date), "MMM d, yyyy") : "N/A"}</TableCell><TableCell><Link to={createPageUrl("AdminMerchantDetail") + `?id=${m.id}`}><Button variant="ghost" size="icon"><Eye className="h-4 w-4" /></Button></Link></TableCell></TableRow>)}</TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        engine={engine}
      />
    </div>
  );
}